import { z } from "zod"
import type { ColumnSizing } from "@/lib/column-widths"

export const COLUMNS = [
  "number",
  "description",
  "category",
  "custodian",
  "calibration_due_on",
  "calibration_in",
  "home_location",
  "classification",
  "last_calibrated_on",
  "manufacturer",
  "serial_number",
] as const

export const columnSchema = z.enum(COLUMNS)

export type Column = z.infer<typeof columnSchema>

export const DEFAULT_COLUMNS: Column[] = [
  "number",
  "description",
  "category",
  "custodian",
  "calibration_due_on",
  "calibration_in",
  "home_location",
  "classification",
  "last_calibrated_on",
]

export const COLUMN_LABELS: Record<Column, string> = {
  number: "Tool ID",
  description: "Description",
  category: "Category",
  custodian: "Custodian",
  calibration_due_on: "Cal due",
  calibration_in: "In",
  home_location: "Location",
  classification: "Class",
  last_calibrated_on: "Last cal",
  manufacturer: "Manufacturer",
  serial_number: "Serial no.",
}

export const SORTABLE = [
  "number",
  "description",
  "category",
  "calibration_due_on",
  "home_location",
  "classification",
  "last_calibrated_on",
  "manufacturer",
  "serial_number",
] as const

export const sortSchema = z.enum(SORTABLE)

export type ToolSort = z.infer<typeof sortSchema>

export function isSortable(column: Column): column is ToolSort {
  return SORTABLE.some((sortable) => sortable === column)
}

export type Slot = Column | "status"

export const COLUMN_SIZING: ColumnSizing<Slot> = {
  id: "tools",
  widths: {
    number: 8,
    status: 2,
    description: 12,
    category: 7,
    custodian: 5,
    calibration_due_on: 8,
    calibration_in: 6,
    home_location: 7,
    classification: 9,
    last_calibrated_on: 6,
    manufacturer: 8,
    serial_number: 6,
  },
  fill: "description",
  pinned: ["status"],
  extras: ["status"],
  dropOrder: [
    "serial_number",
    "manufacturer",
    "last_calibrated_on",
    "classification",
    "home_location",
    "category",
    "calibration_in",
    "custodian",
  ],
}

export function withToolNumber(columns: Column[]): Column[] {
  return COLUMNS.filter(
    (column) => column === "number" || columns.includes(column),
  )
}

export function slotsOf(columns: Column[]): Slot[] {
  const rest = COLUMNS.filter(
    (column) => column !== "number" && columns.includes(column),
  )
  return ["number", "status", ...rest]
}
